import { FC, MouseEvent, useEffect, useRef, useState } from 'react'
import { PlayIcon } from './icons'

const BORDERED_CONTAINER_STYLES = {
  border: '4px solid #1D1D1F',
  borderRadius: '26px',
  boxShadow: '0 0 30px 0 rgba(0, 0, 0, 0.30)',
}

const CONTROLS_HOVER_STYLES = `
  .video-controls-on-hover::-webkit-media-controls {
    opacity: 0;
    transition: opacity 0.3s;
  }
  .video-controls-on-hover:hover::-webkit-media-controls {
    opacity: 0.5;
  }
`

const PLAY_BUTTON_STYLES = {
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '50%',
  height: 70,
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 70,
}

type VideoPlayerProps = {
  src: string
  poster: string
  className?: string
  autoFullscreen?: boolean
  pauseOnExitFullscreen?: boolean
  showControls?: boolean
}

const VideoPlayer: FC<VideoPlayerProps> = ({
  autoFullscreen = true,
  className = '',
  pauseOnExitFullscreen = true,
  poster,
  showControls = false,
  src,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hasPlayedRef = useRef(false)
  const isPreviewingRef = useRef(false)
  const [hasPlayed, setHasPlayed] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement
      setIsFullscreen(isNowFullscreen)

      if (!isNowFullscreen && pauseOnExitFullscreen && videoRef.current) {
        videoRef.current.pause()
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [pauseOnExitFullscreen])

  const handlePlay = () => {
    const video = videoRef.current
    if (!video) return

    hasPlayedRef.current = true
    setHasPlayed(true)
    void video.play()
    if (autoFullscreen && video.requestFullscreen) {
      void video.requestFullscreen()
    }
  }

  const startPreview = () => {
    const video = videoRef.current
    if (hasPlayedRef.current || isPreviewingRef.current || !video) return

    isPreviewingRef.current = true
    video.muted = true
    void video.play()
  }

  const stopPreview = () => {
    const video = videoRef.current
    if (!isPreviewingRef.current || !video || hasPlayedRef.current) return

    video.pause()
    video.currentTime = 0
    isPreviewingRef.current = false
  }

  const handleVideoClick = (event: MouseEvent<HTMLVideoElement>) => {
    const video = videoRef.current
    if (!video) return

    // First click from preview: keep playing, unmute, and mark as played
    if (!hasPlayed) {
      event.preventDefault()
      event.stopPropagation()
      hasPlayedRef.current = true
      isPreviewingRef.current = false
      setHasPlayed(true)
      video.muted = false
      if (video.paused) {
        void video.play()
      }
      return
    }

    // When native controls are visible and video already played, let the browser handle toggles
    if (showControls) return

    event.preventDefault()
    event.stopPropagation()

    // Subsequent clicks toggle play/pause
    if (video.paused) {
      void video.play()
    } else {
      video.pause()
    }
    return undefined
  }

  if (showControls) {
    return (
      <div className='w-100 d-flex justify-content-center my-10'>
        <style>{CONTROLS_HOVER_STYLES}</style>
        <div
          className='position-relative w-100'
          onMouseEnter={startPreview}
          onMouseLeave={stopPreview}
          style={{
            maxWidth: 1349,
            overflow: 'hidden',
            ...BORDERED_CONTAINER_STYLES,
          }}
        >
          <video
            ref={videoRef}
            className={`w-100 h-100 rounded-lg video-controls-on-hover object-fit-cover ${className}`}
            style={{ borderRadius: '22px' }}
            poster={poster}
            preload='metadata'
            muted={!hasPlayed}
            controls={hasPlayed}
            playsInline
            onClick={handleVideoClick}
          >
            <source src={src} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    )
  }

  return (
    <div
      className='position-relative cursor-pointer overflow-hidden'
      style={{
        width: 460,
        ...BORDERED_CONTAINER_STYLES,
      }}
      onClick={handlePlay}
    >
      <video
        ref={videoRef}
        className={`w-100 ${!isFullscreen ? 'rounded-lg' : ''} ${className}`}
        style={!isFullscreen ? { borderRadius: '22px' } : undefined}
        poster={poster}
        preload='metadata'
      >
        <source src={src} type='video/mp4' />
        Your browser does not support the video tag.
      </video>
      {!hasPlayed && (
        <>
          <div
            className='position-absolute top-0 start-0 w-100 h-100'
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '22px',
              pointerEvents: 'none',
            }}
          />

          <div
            className='d-flex align-items-center justify-content-center position-absolute'
            style={PLAY_BUTTON_STYLES}
          >
            <PlayIcon />
          </div>
        </>
      )}
    </div>
  )
}

export default VideoPlayer
